'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ContactUnitForm, type ContactUnitFormRef } from '@/components/partials'
import { type CreateContactUnitParams } from '@/types/contact-unit'
import { useCreateContactUnit } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const contactUnitFormRef = useRef<ContactUnitFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createContactUnit, isPending: isLoading } = useCreateContactUnit()

  const handleSubmit = (data: CreateContactUnitParams) => {
    createContactUnit(data, {
      onSuccess: async newContactUnit => {
        setIsRedirecting(true)
        queryClient.setQueryData(['contact-unit', newContactUnit.id], newContactUnit)
        await queryClient.invalidateQueries({ queryKey: ['contact-units'] })
        toast.success(t('created_successfully'))
        router.push(`/contact-units/${newContactUnit.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateContactUnitParams) => {
    createContactUnit(data, {
      onSuccess: async newContactUnit => {
        queryClient.setQueryData(['contact-unit', newContactUnit.id], newContactUnit)
        await queryClient.invalidateQueries({ queryKey: ['contact-units'] })
        const detailUrl = `/contact-units/${newContactUnit.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        contactUnitFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (contactUnitFormRef.current) {
        contactUnitFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/contact-units')
  }

  return (
    <ContactUnitForm
      key={formKey}
      ref={contactUnitFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
